// src/components/PublishSteps/PublishSteps.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Grid,
  Typography,
  Button,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import PublishStepsHeader from "../PublishStepsHeader";
import { extractPdfInfo } from "./pdfUtils";
import PdfReviewStage from "./PdfReviewStage";
import CoverReadyFlow from "./CoverReadyFlow";
import TitleInformationForm from "./TitleInformationForm";
import CoverageDock from "./CoverageDock";
import useCoverageAnalysis from "./useCoverageAnalysis";
import { createBookProduct, createBookUploadUrls, uploadBookFile } from "services/books";

const primaryButtonSx = {
  color: "#fff !important",
  fontWeight: 800,
  textTransform: "none",
  boxShadow: "0 10px 24px rgba(8, 127, 35, 0.22)",
  "&:hover": {
    color: "#fff !important",
    boxShadow: "0 12px 28px rgba(8, 127, 35, 0.28)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.72) !important",
  },
};

const outlineButtonSx = {
  color: "#172033 !important",
  borderColor: "#b8c3cf",
  fontWeight: 800,
  textTransform: "none",
  "&:hover": {
    borderColor: "#7b8794",
    bgcolor: "#f4f7fb",
  },
};

// Extrai ID do YouTube
function extractYouTubeID(url) {
  const regex = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?#]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

const publishProgressSteps = [
  "Validando arquivos",
  "Preparando links de upload",
  "Enviando miolo, capa e imagem",
  "Cadastrando produto e capa na loja",
  "Finalizando publicação",
];

function PublishProgressDialog({ open, stepIndex, status, message, onClose }) {
  const progress =
    status === "success"
      ? 100
      : status === "error"
      ? Math.max(8, (stepIndex / publishProgressSteps.length) * 100)
      : Math.max(8, ((stepIndex + 1) / publishProgressSteps.length) * 100);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 26px 80px rgba(15, 23, 42, 0.36)",
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(15, 23, 42, 0.68)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#172033",
          fontWeight: 900,
        }}
      >
        Publicando livro
        {status === "success" && <CheckCircleOutlineIcon sx={{ color: "#16824a" }} />}
        {status === "error" && <ErrorOutlineIcon sx={{ color: "#e91e63" }} />}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={900} color="#27364a" mb={1}>
          {message || publishProgressSteps[stepIndex] || "Preparando publicação"}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 9,
            borderRadius: 8,
            bgcolor: "#e8edf3",
            "& .MuiLinearProgress-bar": {
              borderRadius: 8,
              bgcolor: status === "error" ? "#e91e63" : "#087f23",
            },
          }}
        />

        <Box mt={2}>
          {publishProgressSteps.map((step, index) => {
            const isDone = status === "success" || index < stepIndex;
            const isCurrent = index === stepIndex && status === "running";
            const isError = index === stepIndex && status === "error";

            return (
              <Box key={step} display="flex" alignItems="center" gap={1} mb={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: isError
                      ? "#e91e63"
                      : isDone
                      ? "#16824a"
                      : isCurrent
                      ? "#087f23"
                      : "#c8d1dc",
                  }}
                />
                <Typography
                  variant="body2"
                  color={isCurrent || isError ? "#172033" : "#64748b"}
                  fontWeight={isCurrent || isError ? 800 : 500}
                >
                  {step}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      {(status === "error" || status === "success") && (
        <DialogActions sx={{ px: 2.5, py: 2, bgcolor: "#f8fafc" }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: 8,
              px: 2.5,
              color: status === "error" ? "#e91e63" : "#16824a",
              bgcolor: status === "error" ? "#fee7ec" : "#e8f8f0",
              textTransform: "none",
              fontWeight: 800,
              "&:hover": { bgcolor: status === "error" ? "#fbd0da" : "#d7f1e4" },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

function PublishSteps({ user }) {
  const [activeStep, setActiveStep] = useState(0);

  const [uploadedPDF, setUploadedPDF] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const coverage = useCoverageAnalysis(uploadedPDF, { requireReceipt: true });
  const coverageReady = coverage.state.status === "complete" && Boolean(coverage.state.receipt);

  const [dragOver, setDragOver] = useState(false);
  const [showDetectedModal, setShowDetectedModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Novo estado para fluxo da capa
  const [publishedBook, setPublishedBook] = useState(null);
  const [fileError, setFileError] = useState("");
  const [showCoverFlow, setShowCoverFlow] = useState(false);
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [isSavingBook, setIsSavingBook] = useState(false);
  const [coverFile, setCoverFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [bookPaper, setBookPaper] = useState(null);
  const [titleInformationDraft, setTitleInformationDraft] = useState(null);
  const [publishProgress, setPublishProgress] = useState({
    open: false,
    stepIndex: 0,
    status: "running",
    message: "",
  });

  const tutorialVideos = [
    {
      title: "Como fazer uma diagramação básica",
      url: "https://www.youtube.com/watch?v=ZdF9eUI8uM0",
    },
    {
      title: "Como ajustar o formato e as margens no Word",
      url: "https://www.youtube.com/watch?v=G9kz-tag04U",
    },
    {
      title: "Como fazer uma revisão básica pelo Word",
      url: "https://www.youtube.com/watch?v=76m2kmsAxhA",
    },
  ];

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activeStep, showPreview, showCoverFlow, showInfoForm]);

  // Seleção normal
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) processPDF(file);
  };

  // Drag & drop
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processPDF(file);
  };

  const processPDF = async (file) => {
    if (file.type !== "application/pdf") {
      setFileError("Selecione um arquivo PDF para o miolo.");
      return;
    }

    if (file.size > 150 * 1024 * 1024) {
      setFileError("O miolo deve ter até 150 MB.");
      return;
    }

    setFileError("");
    try {
      const info = await extractPdfInfo(file);
      setUploadedPDF(file);
      setPdfInfo(info);
      setShowDetectedModal(true);
    } catch (_) {
      setFileError("Não foi possível abrir o PDF. Confira o arquivo e tente novamente.");
    }
  };

  const removePDF = () => {
    setPublishedBook(null);
    setTitleInformationDraft(null);
    setUploadedPDF(null);
    setPdfInfo(null);
    setShowPreview(false);
    setShowCoverFlow(false);
    setShowInfoForm(false);
    setCoverFile(null);
    setCoverImage(null);
    setBookPaper(null);
    setActiveStep(0);
  };

  const dataUrlToBlob = async (dataUrl) => {
    const response = await fetch(dataUrl);
    return response.blob();
  };

  const buildCoverImagePayload = () => {
    if (!coverImage?.dataUrl) return null;

    const [metadata = "", data = ""] = coverImage.dataUrl.split(",");
    const type = metadata.match(/^data:(.*);base64$/)?.[1] || "image/jpeg";

    return {
      name: coverImage.name,
      type,
      data,
    };
  };

  const handleSaveTitleInformation = async (titleInformation) => {
    if (!coverageReady) {
      setFileError("Conclua a análise de cobertura do miolo antes de publicar.");
      return;
    }
    setIsSavingBook(true);
    let currentProgressStep = 0;
    setPublishProgress({
      open: true,
      stepIndex: 0,
      status: "running",
      message: "Validando arquivos da publicação",
    });

    try {
      if (!user?.uid) {
        throw new Error("Usuario autenticado nao encontrado.");
      }

      if (!uploadedPDF || !coverFile || !coverImage?.dataUrl) {
        throw new Error("Envie o miolo e a capa antes de cadastrar o livro.");
      }

      const coverImagePayload = buildCoverImagePayload();
      currentProgressStep = 1;
      setPublishProgress({
        open: true,
        stepIndex: 1,
        status: "running",
        message: "Preparando links seguros para upload",
      });
      const uploadUrls = await createBookUploadUrls({
        userId: user.uid,
        title: titleInformation.title,
        isbn: titleInformation.isbn,
        internalCode: titleInformation.internalCode,
        coverImageType: coverImagePayload?.type || "image/jpeg",
      });
      const coverImageBlob = await dataUrlToBlob(coverImage.dataUrl);

      currentProgressStep = 2;
      setPublishProgress({
        open: true,
        stepIndex: 2,
        status: "running",
        message: "Enviando miolo, capa original e imagem do produto",
      });
      await Promise.all([
        uploadBookFile({
          uploadUrl: uploadUrls.files.coverPdf.uploadUrl,
          file: coverFile,
          fileName: uploadUrls.files.coverPdf.fileName,
          contentType: "application/pdf",
          label: "a capa em PDF",
        }),
        uploadBookFile({
          uploadUrl: uploadUrls.files.interiorPdf.uploadUrl,
          file: uploadedPDF,
          fileName: uploadUrls.files.interiorPdf.fileName,
          contentType: "application/pdf",
          label: "o miolo em PDF",
        }),
        uploadBookFile({
          uploadUrl: uploadUrls.files.coverImage.uploadUrl,
          file: coverImageBlob,
          contentType: coverImagePayload?.type || "image/jpeg",
          label: "a imagem da capa",
        }),
      ]);

      currentProgressStep = 3;
      setPublishProgress({
        open: true,
        stepIndex: 3,
        status: "running",
        message: "Cadastrando produto e capa na loja",
      });
      const createdBook = await createBookProduct({
        ...titleInformation,
        coverageReceipt: coverage.state.receipt,
        interiorColor: coverage.state.result?.colored
          ? "color"
          : titleInformation.interiorColor || "bw",
        userId: user.uid,
        productCode: uploadUrls.productCode,
        productEan: uploadUrls.productEan,
        pdf: pdfInfo,
        paper: bookPaper,
        fileUrls: {
          coverPdf: uploadUrls.files.coverPdf.publicUrl,
          interiorPdf: uploadUrls.files.interiorPdf.publicUrl,
          coverImage: uploadUrls.files.coverImage.publicUrl,
        },
        coverImage: coverImagePayload,
        coverImageUrl: uploadUrls.files.coverImage.publicUrl,
      });
      setPublishedBook(createdBook);
      setActiveStep(4);
      const missingProperties = createdBook?.properties?.missing || [];
      const hasPropertiesWarning = Boolean(createdBook?.properties?.warning);

      setPublishProgress({
        open: true,
        stepIndex: 4,
        status: "success",
        message:
          hasPropertiesWarning && missingProperties.length
            ? `Livro cadastrado na loja. Características não encontradas: ${missingProperties.join(
                ", "
              )}.`
            : `Livro cadastrado com sucesso. Código PSO: ${createdBook.internalCode}`,
      });
    } catch (error) {
      setPublishProgress({
        open: true,
        stepIndex: currentProgressStep,
        status: "error",
        message: error?.message || "Nao foi possivel cadastrar o livro na loja.",
      });
    } finally {
      setIsSavingBook(false);
    }
  };

  return (
    <Box
      mt={4}
      className="publish-workspace"
      sx={
        uploadedPDF && !publishedBook
          ? {
              pb: { xs: "240px", sm: "150px" },
              "& .flow-actions": { bottom: { xs: 184, sm: 120 } },
            }
          : undefined
      }
    >
      {uploadedPDF && !publishedBook && <CoverageDock analysis={coverage} />}
      <PublishStepsHeader activeStep={activeStep} />
      {publishedBook && (
        <Card
          sx={{
            p: { xs: 3, md: 5 },
            my: 3,
            textAlign: "center",
            border: "1px solid #dce8df",
            borderRadius: 3,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#147d48", mb: 2 }} />
          <Typography variant="h4" mb={1}>
            Livro cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Sua obra está disponível em Minhas Publicações.
          </Typography>
          <Typography variant="subtitle2" mb={3}>
            Código PSO: {publishedBook.internalCode}
          </Typography>
          {publishedBook.properties?.warning && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              O livro foi criado, mas algumas características precisam de revisão. Confira os dados
              da obra antes de divulgá-la.
            </Alert>
          )}
          <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <Button variant="outlined" onClick={removePDF}>
              Iniciar outra publicação
            </Button>
            <Button component="a" href="/my-publications" variant="contained" color="info">
              Ver minhas publicações
            </Button>
          </Box>
        </Card>
      )}

      {fileError && (
        <Alert severity="error" sx={{ my: 2 }} onClose={() => setFileError("")}>
          {fileError}
        </Alert>
      )}
      <PublishProgressDialog
        open={publishProgress.open}
        stepIndex={publishProgress.stepIndex}
        status={publishProgress.status}
        message={publishProgress.message}
        onClose={() => setPublishProgress((current) => ({ ...current, open: false }))}
      />

      {/* Modal detectado */}
      {showDetectedModal && pdfInfo && (
        <Dialog
          open={showDetectedModal}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 26px 80px rgba(15, 23, 42, 0.36)",
            },
          }}
          BackdropProps={{
            sx: {
              bgcolor: "rgba(15, 23, 42, 0.68)",
              backdropFilter: "blur(3px)",
            },
          }}
        >
          <DialogContent sx={{ px: 3, py: 3, textAlign: "center" }}>
            <Typography variant="subtitle1" fontWeight={900} color="#27364a">
              Detectamos o seguinte tamanho de miolo:
            </Typography>

            <Typography mt={2} color="#172033">
              Tamanho:{" "}
              <b>
                {pdfInfo.widthCm} × {pdfInfo.heightCm} cm
              </b>
            </Typography>

            <Typography mb={3} color="#172033">
              Número de páginas: <b>{pdfInfo.pages}</b>
            </Typography>

            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                sx={{
                  color: "#fff !important",
                  borderColor: "#ff4d4d",
                  backgroundColor: "#ff4d4d",
                  fontWeight: 800,
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#e53935",
                    color: "#fff !important",
                  },
                }}
                onClick={() => {
                  removePDF();
                  setShowDetectedModal(false);
                }}
              >
                Vou refazer
              </Button>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#28a745",
                  color: "#fff !important",
                  fontWeight: 800,
                  textTransform: "none",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#218838",
                    color: "#fff !important",
                  },
                }}
                onClick={() => {
                  setShowDetectedModal(false);
                  setShowPreview(true);
                }}
              >
                Revisar miolo
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* --- ETAPA 2: FLIPBOOK DO MIOLO --- */}
      {showPreview && uploadedPDF && !showCoverFlow && !showInfoForm && (
        <PdfReviewStage
          file={uploadedPDF}
          pdfInfo={pdfInfo}
          onRefazer={removePDF}
          onProsseguir={() => {
            setShowCoverFlow(true); // Agora abre o fluxo da capa
            setActiveStep(1);
          }}
        />
      )}

      {/* --- ETAPA 3: FLUXO DA CAPA --- */}
      {!publishedBook && (showCoverFlow || showInfoForm) && (
        <Box sx={{ display: showInfoForm ? "none" : "block" }}>
          <CoverReadyFlow
            pdfInfo={pdfInfo}
            onClose={() => {
              setShowCoverFlow(false);
              setShowPreview(true);
            }}
            onFinish={(selectedCoverFile, selectedCoverImageDataUrl, selectedPaper) => {
              setCoverFile(selectedCoverFile);
              setCoverImage({
                name: selectedCoverFile?.name || "capa.jpg",
                dataUrl: selectedCoverImageDataUrl,
              });
              setBookPaper(selectedPaper || null);

              setShowCoverFlow(false);
              setShowInfoForm(true);
              setActiveStep(2);
            }}
          />
        </Box>
      )}

      {/* --- ETAPA 4: INFORMAÇÕES DO TÍTULO --- */}
      {showInfoForm && !publishedBook && (
        <TitleInformationForm
          isSaving={isSavingBook}
          coverageResult={coverage.state.result}
          publicationBlocked={!coverageReady}
          pdfInfo={pdfInfo}
          paper={bookPaper}
          user={user}
          draft={titleInformationDraft}
          onDraftChange={setTitleInformationDraft}
          onClose={() => {
            setShowInfoForm(false);
            setShowCoverFlow(true);
            setActiveStep(1);
          }}
          onSave={handleSaveTitleInformation}
        />
      )}

      {/* --- TELA INICIAL – antes do preview --- */}
      {!showPreview && !showCoverFlow && !showInfoForm && (
        <Grid container spacing={3} mt={3}>
          {/* COLUNA ESQUERDA */}
          <Grid item xs={12} md={6}>
            <Card sx={{ padding: 3 }}>
              <Typography variant="h6" mb={1}>
                UPLOAD DO MIOLO
              </Typography>

              <Typography variant="body2" mb={3}>
                Ofereça uma excelente experiência aos seus leitores. Veja os vídeos tutoriais:
              </Typography>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Vídeos Tutoriais</Typography>
                </AccordionSummary>

                <AccordionDetails>
                  {tutorialVideos.map((v, i) => {
                    const id = extractYouTubeID(v.url);
                    const thumb = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

                    return (
                      <Box
                        key={i}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        mb={2}
                        sx={{ cursor: "pointer" }}
                        onClick={() => window.open(v.url, "_blank")}
                      >
                        <Box
                          component="img"
                          src={thumb}
                          alt="thumbnail"
                          sx={{
                            width: 110,
                            height: 70,
                            objectFit: "cover",
                            borderRadius: "6px",
                          }}
                        />

                        <Typography>{v.title}</Typography>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">Informações Complementares</Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <ul>
                    <li>
                      <b>Tamanhos aceitos:</b> 7×11 cm até 21×29,7 cm (A4)
                    </li>
                    <li>
                      <b>Acabamentos:</b> 4–56 páginas (grampeado), 57–1000 (brochura)
                    </li>
                    <li>
                      <b>Prefira tamanhos até 16×23 cm</b>
                    </li>
                    <li>
                      <b>Limite:</b> 150MB
                    </li>
                  </ul>
                </AccordionDetails>
              </Accordion>
            </Card>
          </Grid>

          {/* COLUNA DIREITA */}
          <Grid item xs={12} md={6}>
            <Card sx={{ padding: 3 }}>
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                sx={{
                  width: "100%",
                  minHeight: "260px",
                  border: `2px dashed ${dragOver ? "#06651c" : "#087f23"}`,
                  backgroundColor: dragOver ? "#e5f7e9" : "transparent",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  transition: "0.2s",
                }}
              >
                {!uploadedPDF && (
                  <>
                    <Typography fontWeight="bold" mb={1}>
                      Arraste e solte o PDF aqui
                    </Typography>

                    <Typography variant="body2" mb={2}>
                      – OU –
                    </Typography>

                    <input
                      type="file"
                      accept="application/pdf"
                      id="pdfInput"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                    />

                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#087f23",
                        ...primaryButtonSx,
                        borderRadius: "30px",
                        px: 4,
                      }}
                      onClick={() => document.getElementById("pdfInput").click()}
                    >
                      Selecionar PDF
                    </Button>
                  </>
                )}

                {uploadedPDF && (
                  <Box textAlign="center">
                    <Typography fontWeight="bold" color="#087f23" mb={1}>
                      PDF carregado: {uploadedPDF.name}
                    </Typography>

                    <Button
                      variant="outlined"
                      startIcon={<DeleteForeverIcon />}
                      onClick={removePDF}
                      sx={outlineButtonSx}
                    >
                      Remover PDF
                    </Button>

                    <Button
                      sx={{ mt: 2, ...primaryButtonSx }}
                      variant="contained"
                      onClick={() => setShowPreview(true)}
                    >
                      Revisar miolo
                    </Button>
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default PublishSteps;
